import type { ApiResponse, RequestConfig } from "./api";
import type { Where } from "@/types/backend-filter";
import { api } from "./api";

export type CrudID = string | number;

export interface CrudOptions {
  onError?: (err: unknown) => void;
  hideError?: boolean;
}

export interface Crud<Response, Body = Response> {
  count: (
    where?: Where,
    axiosConfig?: RequestConfig,
    options?: CrudOptions
  ) => Promise<number>;
  get: (config?: RequestConfig, options?: CrudOptions) => Promise<Response[]>;
  getById: (id: CrudID, config?: object, options?: CrudOptions) => Promise<Response | undefined>;
  create: (body: Body, config?: RequestConfig, options?: CrudOptions) => Promise<Response>;
  updateById: (
    id: CrudID,
    body: Partial<Body>,
    config?: RequestConfig,
    options?: CrudOptions
  ) => Promise<void>;
  replaceById: (id: CrudID, body: Partial<Body>, options?: CrudOptions) => Promise<void>;
  updateAll(body: Partial<Body>, where: Where, options?: CrudOptions): Promise<void>;
  deleteById: (id: CrudID, options?: CrudOptions) => Promise<void>;
  deleteMany: (ids: CrudID[], options?: CrudOptions) => Promise<void>;
}

export function createLoopbackCrud<Response, Body = Response>(url: string): Crud<Response, Body> {
  async function count(where?: Where, axiosConfig: RequestConfig = {}) {
    const { data } = (await api.get(
      `${url}/count`,
      where && { params: { where }, ...axiosConfig }
    )) as ApiResponse<{ count: number }>;
    return data.count;
  }

  async function get(config?: RequestConfig) {
    const { data } = (await api(url, config)) as ApiResponse<Response[]>;
    return data;
  }

  async function getById(id: CrudID, config?: RequestConfig) {
    const { data } = (await api(`${url}/${id}`, config)) as ApiResponse<Response>;
    return data;
  }

  async function create(body: Body, config?: RequestConfig) {
    const { data } = (await api.post(url, body, config)) as ApiResponse<Response>;
    return data;
  }

  async function updateById(id: CrudID, body: Partial<Body>, config?: RequestConfig) {
    await api.patch(`${url}/${id}`, body, config);
  }

  async function replaceById(id: CrudID, body: Partial<Body>) {
    await api.put(`${url}/${id}`, body);
  }

  async function updateAll(body: Partial<Body>, where: Where) {
    await api.patch(url, body, {
      params: {
        where,
      },
    });
  }

  async function deleteById(id: CrudID) {
    await api.delete(`${url}/${id}`);
  }

  async function deleteMany(ids: CrudID[]) {
    await api({
      method: "DELETE",
      url,
      params: {
        where: {
          id: {
            inq: ids,
          },
        },
      },
    });
  }

  return {
    count,
    get,
    getById,
    create,
    updateById,
    updateAll,
    deleteById,
    deleteMany,
    replaceById,
  };
}
